import { useState } from 'react';
import { PencilIcon, XMarkIcon } from '@heroicons/react/24/outline';
import ApiClient from '../utils/ApiClient';

// Task 24/41/67/69/39/80 (owner requirements, see ShipmentPlanner.js history): the box
// configuration modal's state and handlers -- two-tier box model (owner, verbatim: "the user
// should specify the weight of the unit, and also specify the weight and qty of boxes to make
// the shipment. and the total is added together ... the box size and weights can be shared
// across asins, the shipment configuration should be asin specific though"). Task 1 extracted
// the markup to components/BoxConfigModal.js; this hook extracts the behaviour behind it so a
// second host (PO Creation) can open the same modal against the same UNIT/CARTON/parcel-ceiling
// rules instead of forking them -- owner decision 2026-09-13: share the logic, don't copy it.
//
// This hook owns only state that belongs to the modal itself. A few things it touches are
// genuinely planner-wide (the per-ASIN packaging map, PO-confirm bookkeeping, the toast, the
// shipment mode, and a handful of pure helpers that are also used, and unit-tested, outside the
// modal) -- those are supplied by the host rather than duplicated or pulled in by import, both to
// keep a single source of truth and to avoid a pages/hooks import cycle (ShipmentPlanner.js is
// the one importing this hook). See the box-hook-report for the full accounting of that coupling.
export function useBoxConfig({
    // Host-owned read inputs -- used to seed/validate the modal; not modal state.
    shipmentMode,
    packagingByAsin,
    poConfirmDefaultsByAsin,
    poPackagingMetaByAsin,
    // Host-owned setters the save path writes through. packagingByAsin is the planner's per-ASIN
    // analysis map; poConfirmConfirmed/packagingConfirmedAsin are the PO-confirm-step and
    // green-tick bookkeeping; setToast is the page-level toast. None of these belong to the modal
    // -- they are planner state that outlives the modal being open.
    setPackagingByAsin,
    setToast,
    setPoConfirmConfirmed,
    setPackagingConfirmedAsin,
    // Pure helpers/formatters that live in ShipmentPlanner.js (and are unit-tested by importing
    // them from there directly, so they cannot be renamed or relocated) -- passed in rather than
    // imported to avoid the pages/hooks cycle noted above.
    isPackagingComplete,
    fmtLbsShort,
    computeLoadedBoxWeightLbs,
    deriveLegacyPackagingFields,
    computeShipmentTotals,
    boxFormFromCatalogBox,
    boxCatalogErrorText,
    unitBoxEntryFromDerived,
    unitBoxMatchesDerived,
    defaultPackagingStyle,
    // Called once a save has applied -- lets the host refresh. The planner already keeps its own
    // packagingByAsin current via the setter above (so this is a no-op there); PO Creation, which
    // has no such map, uses this to refetch its suggestions.
    onSaved,
    // Finding 1 fix (task-3 report): the planner's "apply locally, then best-effort persist as
    // the default" model is wrong on a host with no shipment context (PO Creation) -- there, the
    // POST to /api/shipment-planner/packaging IS the entire operation, not a nice-to-have. Default
    // false keeps the planner's behaviour bit-for-bit unchanged (local apply/close/onSaved fire
    // BEFORE the best-effort POST, whose rejection only ever produces a toast). true awaits the
    // POST FIRST: on success it applies/closes/fires onSaved same as before; on rejection it
    // leaves the popover open, never fires onSaved, and reports the failure via the returned
    // `saveError` instead of a toast string the host would otherwise have to pattern-match.
    requirePersist = false,
}) {
    const [packagingPopoverAsin, setPackagingPopoverAsin] = useState(null);
    // Task 39 (owner, with screenshot): the modal header shows the product title alongside the
    // ASIN when the calling row has one (main plan rows, PO-import Step 2 rows) -- falls back to
    // ASIN-only when not available. Purely a header label; never sent to the API.
    const [packagingPopoverTitle, setPackagingPopoverTitle] = useState(null);
    // Task 41 (two-tier box model): the modal's form is the ASIN-specific shipment configuration
    // -- unit weight + units per box + WHICH shared catalog carton. The loaded box weight is never
    // typed; it's computed live (units x unit weight + tare) and shown as spelled-out math.
    // Task 67: boxLengthIn/boxWidthIn/boxHeightIn are used by the Single-unit (unit = box) style
    // only -- the Carton style keeps taking dims from the shared catalog carton.
    const [packagingForm, setPackagingForm] = useState({
        unitsPerBox: '', unitWeightLbs: '', boxLengthIn: '', boxWidthIn: '', boxHeightIn: '',
    });
    // Task 67 (owner: "maybe a toggle that would allow for carton vs single unit 'boxes'?"):
    // the modal's two-way style toggle + the open ASIN's Amazon-derived unit-box suggestion.
    const [packagingStyleChoice, setPackagingStyleChoice] = useState('CARTON');
    const [packagingModalDerived, setPackagingModalDerived] = useState(null);
    const [packagingSaving, setPackagingSaving] = useState(false);
    const [packagingAttemptedSubmit, setPackagingAttemptedSubmit] = useState(false);
    // requirePersist:true's own failure surface (Finding 1) -- set when the persist-first POST
    // rejects, cleared on every new attempt and whenever the popover opens/closes. Always null
    // under requirePersist:false (the planner keeps reporting persist failures via setToast,
    // unchanged).
    const [saveError, setSaveError] = useState(null);
    // The row qty the modal was opened from (plan row / PO-confirm row) -- drives the
    // "50 units → 10 boxes · total 290 lb" line. Null when opened without qty context.
    const [packagingPopoverQty, setPackagingPopoverQty] = useState(null);

    // Task 41: the SHARED box catalog (GET /box-catalog -- physical cartons: name, dims, tare),
    // one flat list reused across every ASIN (supersedes the ASIN-scoped presets of tasks
    // 22/37/39). selectedBoxId is the modal's chosen carton; the "New box…" card expands an
    // inline name+dims+tare form that saves to the catalog for everyone.
    const [boxCatalog, setBoxCatalog] = useState([]);
    const [selectedBoxId, setSelectedBoxId] = useState(null);
    const [newBoxOpen, setNewBoxOpen] = useState(false);
    const [newBoxForm, setNewBoxForm] = useState({
        name: '', boxLengthIn: '', boxWidthIn: '', boxHeightIn: '', tareWeightLbs: '',
    });
    const [newBoxSaving, setNewBoxSaving] = useState(false);
    const [boxNote, setBoxNote] = useState(null);
    const [boxDeletingId, setBoxDeletingId] = useState(null);
    // 2026-09-01: editing an existing carton -- the card's pencil prefills the form; while set,
    // the name is locked so Save can only hit the upsert's UPDATE path for this box.
    const [editingBoxId, setEditingBoxId] = useState(null);
    // 2026-09-01: save/delete failures render INSIDE the modal (same lesson as analysisError:
    // the page-top toast is invisible behind a full-screen overlay, so refusals looked like
    // dead clicks -- notably delete's "box is in use by N ASIN configuration(s)").
    const [boxError, setBoxError] = useState(null);

    // Task 41: this ASIN's currently-chosen shared carton -- the loaded box weight (units x
    // unit weight + tare) is derived from it, never typed. Task 67: for the Single-unit (unit =
    // box) style there is no shared carton -- the unit's own package IS the box, dims/weight come
    // off the form directly, and box weight = unit weight.
    const selectedBox = boxCatalog.find(b => b.id === selectedBoxId) || null;

    const validatePackagingForm = (form) => {
        const errors = {};
        const unitsPerBox = parseInt(form.unitsPerBox, 10);
        const unitWeightLbs = parseFloat(form.unitWeightLbs);

        if (!(unitsPerBox > 0)) errors.unitsPerBox = 'Required';
        if (!(unitWeightLbs > 0)) errors.unitWeightLbs = 'Required';
        if (!selectedBox) errors.box = 'Pick a box (or create one)';

        const loaded = selectedBox
            ? computeLoadedBoxWeightLbs(unitsPerBox, unitWeightLbs, selectedBox.tareWeightLbs)
            : null;

        // Task 3 (owner decision): shipmentMode is null when the host has an ASIN designated
        // allowed_modes=BOTH and the operator hasn't yet picked which one this box is being
        // configured for. Unknown is never zero -- no ceiling is assumed here (neither UPS's 150
        // lb nor LTL's 500 lb), so the config is blocked on the missing choice instead of being
        // silently judged against a guessed cap.
        if (shipmentMode == null) {
            errors.mode = 'Choose a shipment mode (UPS or LTL) for this box before saving.';
        } else {
            // Task 61: LTL freight has no 150 lb parcel ceiling -- only the 500 lb hard cap applies.
            const weightCap = shipmentMode === 'LTL' ? 500 : 150;
            const capLabel = shipmentMode === 'LTL' ? '500 lb LTL hard cap' : '150 lb parcel ceiling';
            if (loaded != null && loaded > weightCap) {
                errors.loaded = `${unitsPerBox} × ${fmtLbsShort(unitWeightLbs)} lb + ${fmtLbsShort(selectedBox.tareWeightLbs)} lb box = ${fmtLbsShort(loaded)} lb — over the ${capLabel}`;
            }
        }

        return {
            parsed: {
                unitsPerBox: unitsPerBox > 0 ? unitsPerBox : null,
                unitWeightLbs: unitWeightLbs > 0 ? unitWeightLbs : null,
            },
            loaded,
            errors,
        };
    };
    // Task 67: Single-unit (unit = box) validation -- units/box is locked to 1, dims come off the
    // form (typically the Amazon prefill), the unit weight IS the box weight (no tare). The same
    // per-mode ceilings apply as everywhere else: UPS 108 in / 150 lb, LTL 96 in / 500 lb.
    const validateUnitBoxForm = (form) => {
        const errors = {};
        const uw = parseFloat(form.unitWeightLbs);
        const len = parseFloat(form.boxLengthIn);
        const wid = parseFloat(form.boxWidthIn);
        const hgt = parseFloat(form.boxHeightIn);
        if (!(uw > 0)) errors.unitWeightLbs = 'Required';
        if (!(len > 0)) errors.boxLengthIn = 'Required';
        if (!(wid > 0)) errors.boxWidthIn = 'Required';
        if (!(hgt > 0)) errors.boxHeightIn = 'Required';

        // Task 3 (owner decision): see validatePackagingForm's identical branch above -- no
        // ceiling (dimension or weight) is assumed while the host has a BOTH-designated ASIN with
        // no mode chosen yet.
        if (shipmentMode == null) {
            errors.mode = 'Choose a shipment mode (UPS or LTL) for this box before saving.';
        } else {
            const dimCap = shipmentMode === 'LTL' ? 96 : 108;
            const weightCap = shipmentMode === 'LTL' ? 500 : 150;
            const capLabel = shipmentMode === 'LTL' ? 'LTL hard cap' : 'parcel ceiling';
            if ([len, wid, hgt].some(d => d > dimCap)) {
                errors.loaded = `a dimension exceeds the ${dimCap} in ${capLabel} — this unit can't ship as its own box${shipmentMode === 'LTL' ? '' : ' via small parcel'}`;
            } else if (uw > weightCap) {
                errors.loaded = `${fmtLbsShort(uw)} lb — over the ${weightCap} lb ${capLabel}`;
            }
        }
        return {
            parsed: {
                unitWeightLbs: uw > 0 ? uw : null,
                boxLengthIn: len > 0 ? len : null,
                boxWidthIn: wid > 0 ? wid : null,
                boxHeightIn: hgt > 0 ? hgt : null,
            },
            loaded: uw > 0 ? uw : null,
            errors,
        };
    };

    const { errors: packagingFieldErrors, loaded: packagingLoadedLbs } =
        packagingStyleChoice === 'UNIT' ? validateUnitBoxForm(packagingForm) : validatePackagingForm(packagingForm);
    const packagingFormValid = Object.keys(packagingFieldErrors).length === 0;

    // Task 41: the shared box catalog -- one flat list for every ASIN, fetched once per session
    // (and refreshed after any save/delete).
    const loadBoxCatalog = async () => {
        try {
            const res = await ApiClient.get('/api/shipment-planner/box-catalog');
            const boxes = res.data || [];
            setBoxCatalog(boxes);
            return boxes;
        } catch (err) {
            console.error('Failed to load box catalog:', err);
            return boxCatalog;
        }
    };

    // Opens the packaging modal for an ASIN. Pre-fills from the ASIN's current working value when
    // it carries the two-tier config (unit weight + catalog box); a legacy value (five fields
    // typed before task 41) pre-fills units/box only and shows a "legacy" note -- re-entering the
    // unit weight + picking a box switches it to computed. qty (the calling row's quantity, when
    // known) drives the "N units → M boxes · total X lb" line.
    const openPackagingPopover = async (asin, title, qty) => {
        setPackagingPopoverAsin(asin);
        setPackagingPopoverTitle(title || null);
        setPackagingPopoverQty(qty > 0 ? qty : null);
        setPackagingSaving(false);
        setPackagingAttemptedSubmit(false);
        setNewBoxOpen(false);
        setNewBoxForm({ name: '', boxLengthIn: '', boxWidthIn: '', boxHeightIn: '', tareWeightLbs: '' });
        setBoxNote(null);
        setEditingBoxId(null);
        setBoxError(null);
        setSaveError(null);

        const pkg = packagingByAsin[asin];
        // Task 67: toggle default = working style > saved style > "nothing configured but Amazon
        // knows the unit box" -> UNIT > CARTON. The suggestion itself rides on the working entry
        // (packagingEntryFromRow carries it) or the PO-confirm/PO-lines meta caches.
        const metaRow = poConfirmDefaultsByAsin[asin] ?? poPackagingMetaByAsin[asin] ?? null;
        const derived = pkg?.derivedUnitBox ?? metaRow?.derivedUnitBox ?? null;
        const style = defaultPackagingStyle(pkg, metaRow);
        setPackagingModalDerived(derived);
        setPackagingStyleChoice(style);
        const unitSource = pkg?.style === 'UNIT' ? pkg : unitBoxEntryFromDerived(derived);
        setPackagingForm(style === 'UNIT'
            ? {
                unitsPerBox: 1,
                unitWeightLbs: unitSource?.boxWeightLbs ?? '',
                boxLengthIn: unitSource?.boxLengthIn ?? '',
                boxWidthIn: unitSource?.boxWidthIn ?? '',
                boxHeightIn: unitSource?.boxHeightIn ?? '',
            }
            : {
                unitsPerBox: pkg?.unitsPerBox ?? '',
                unitWeightLbs: pkg?.unitWeightLbs ?? '',
                boxLengthIn: '', boxWidthIn: '', boxHeightIn: '',
            });
        setSelectedBoxId(pkg?.boxCatalogId ?? null);
        loadBoxCatalog();
    };

    // Task 67: switching styles re-seeds the form for the target style without losing the other
    // side's values mid-session more than necessary -- UNIT locks units/box to 1 and auto-fills
    // dims+weight from the current unit-style value or the Amazon suggestion (still editable);
    // CARTON restores the working carton numbers.
    const choosePackagingStyle = (style) => {
        if (style === packagingStyleChoice) return;
        setPackagingStyleChoice(style);
        setPackagingAttemptedSubmit(false);
        const pkg = packagingByAsin[packagingPopoverAsin];
        if (style === 'UNIT') {
            const unitSource = pkg?.style === 'UNIT' ? pkg : unitBoxEntryFromDerived(packagingModalDerived);
            setPackagingForm(prev => ({
                ...prev,
                unitsPerBox: 1,
                unitWeightLbs: unitSource?.boxWeightLbs ?? '',
                boxLengthIn: unitSource?.boxLengthIn ?? '',
                boxWidthIn: unitSource?.boxWidthIn ?? '',
                boxHeightIn: unitSource?.boxHeightIn ?? '',
            }));
        } else {
            setPackagingForm(prev => ({
                ...prev,
                unitsPerBox: pkg?.style !== 'UNIT' && pkg?.unitsPerBox ? pkg.unitsPerBox : '',
                unitWeightLbs: pkg?.style !== 'UNIT' && pkg?.unitWeightLbs ? pkg.unitWeightLbs : '',
            }));
        }
    };

    const closePackagingPopover = () => {
        setPackagingPopoverAsin(null);
        setPackagingPopoverTitle(null);
        setPackagingPopoverQty(null);
        setSaveError(null);
    };

    const updatePackagingField = (field, value) => {
        setPackagingForm(prev => ({ ...prev, [field]: value }));
    };

    const updateNewBoxField = (field, value) => {
        setNewBoxForm(prev => ({ ...prev, [field]: value }));
    };

    // "New box…" save -- validates locally (name, dims ≤ 108 in, tare > 0; the server re-checks),
    // POSTs to the shared catalog, then selects the saved carton for this ASIN.
    const saveNewBoxToCatalog = async () => {
        const name = newBoxForm.name.trim();
        const len = parseFloat(newBoxForm.boxLengthIn);
        const wid = parseFloat(newBoxForm.boxWidthIn);
        const hgt = parseFloat(newBoxForm.boxHeightIn);
        const tare = parseFloat(newBoxForm.tareWeightLbs);
        if (!name) { setBoxError('Box name is required.'); return; }
        if (!(len > 0) || !(wid > 0) || !(hgt > 0) || len > 108 || wid > 108 || hgt > 108) {
            setBoxError('Box dimensions must be > 0 and ≤ 108 in.');
            return;
        }
        if (!(tare > 0)) { setBoxError('Empty (tare) box weight must be > 0.'); return; }
        setNewBoxSaving(true);
        setBoxError(null);
        try {
            const res = await ApiClient.post('/api/shipment-planner/box-catalog', {
                name, boxLengthIn: len, boxWidthIn: wid, boxHeightIn: hgt, tareWeightLbs: tare,
            });
            await loadBoxCatalog();
            if (res.data?.box?.id != null) setSelectedBoxId(res.data.box.id);
            setNewBoxOpen(false);
            setEditingBoxId(null);
            setNewBoxForm({ name: '', boxLengthIn: '', boxWidthIn: '', boxHeightIn: '', tareWeightLbs: '' });
            setBoxNote(res.data?.updatedExisting
                ? `Updated existing box "${name}" — its dims/tare now apply everywhere it's used.`
                : `Saved box "${name}" to the shared catalog.`);
        } catch (err) {
            console.error('Failed to save catalog box:', err);
            setBoxError(boxCatalogErrorText(err, 'save'));
        }
        setNewBoxSaving(false);
    };

    // 2026-09-01: the card's pencil -- prefill the form from the carton and lock the name, so
    // Save can only update THIS box (the backend upserts by name).
    const startEditCatalogBox = (box) => {
        setNewBoxForm(boxFormFromCatalogBox(box));
        setEditingBoxId(box.id);
        setNewBoxOpen(true);
        setBoxError(null);
        setBoxNote(null);
    };

    // Delete a shared carton (hover X, confirm) -- the backend refuses while any ASIN's saved
    // default still references it, which surfaces here as a toast.
    const deleteCatalogBox = async (box) => {
        if (!window.confirm(`Delete box "${box.name}" from the shared catalog? ASIN configurations using it keep their saved dims/weight but lose the link to this box.`)) return;
        setBoxDeletingId(box.id);
        setBoxError(null);
        try {
            const res = await ApiClient.delete(`/api/shipment-planner/box-catalog/${box.id}`);
            await loadBoxCatalog();
            if (selectedBoxId === box.id) setSelectedBoxId(null);
            if (editingBoxId === box.id) { setEditingBoxId(null); setNewBoxOpen(false); }
            const detached = res.data?.detachedAsins || [];
            setBoxNote(`Deleted box "${box.name}" from the shared catalog.`
                + (detached.length ? ` Detached ${detached.length} ASIN configuration(s): ${detached.join(', ')} — their saved dims/weight are unchanged.` : ''));
        } catch (err) {
            console.error('Failed to delete catalog box:', err);
            setBoxError(boxCatalogErrorText(err, 'delete'));
        }
        setBoxDeletingId(null);
    };

    // Shared by both footer actions -- validates, derives the five legacy analyze/watch fields
    // from the config (dims off the carton, weight computed), applies them to this shipment's
    // packagingByAsin (always), and -- only for the "make default" action -- POSTs the two-tier
    // config so the server persists it (and derives the same legacy fields, verified by tests).
    const applyPackaging = async (alsoMakeDefault) => {
        const asin = packagingPopoverAsin;
        if (!asin) return;

        // Task 67: Single-unit (unit = box) apply -- 1/box, dims off the form (usually the
        // Amazon prefill), box weight = unit weight, no shared carton involved.
        if (packagingStyleChoice === 'UNIT') {
            const { parsed: up, errors: uerrors } = validateUnitBoxForm(packagingForm);
            if (Object.keys(uerrors).length > 0) {
                setPackagingAttemptedSubmit(true);
                setToast(uerrors.loaded
                    ? `Unit-as-box configuration rejected: ${uerrors.loaded}`
                    : uerrors.mode
                        ? uerrors.mode
                        : 'Unit weight and all three package dimensions are required for a single-unit configuration.');
                return;
            }
            const unitEntry = {
                unitsPerBox: 1,
                boxLengthIn: up.boxLengthIn,
                boxWidthIn: up.boxWidthIn,
                boxHeightIn: up.boxHeightIn,
                boxWeightLbs: up.unitWeightLbs,
                unitWeightLbs: up.unitWeightLbs,
                boxCatalogId: null,
                boxName: null,
                tareWeightLbs: null,
                computed: false,
                style: 'UNIT',
                unitBoxSource: unitBoxMatchesDerived(
                    { boxLengthIn: up.boxLengthIn, boxWidthIn: up.boxWidthIn, boxHeightIn: up.boxHeightIn, boxWeightLbs: up.unitWeightLbs },
                    packagingModalDerived) ? 'AMAZON' : null,
                derivedUnitBox: packagingModalDerived,
            };
            const postBody = {
                asin,
                packagingStyle: 'UNIT',
                unitsPerBox: 1,
                unitWeightLbs: up.unitWeightLbs,
                boxLengthIn: up.boxLengthIn,
                boxWidthIn: up.boxWidthIn,
                boxHeightIn: up.boxHeightIn,
                mode: shipmentMode,
            };
            // The "it happened" bundle -- local apply, close, un-confirm hooks, onSaved.
            // requirePersist:false (planner) runs this unconditionally, before the best-effort
            // POST below even starts (bit-for-bit the old behaviour). requirePersist:true (PO
            // Creation -- no shipment to apply to) runs it ONLY after the POST, the entire
            // operation on that host, has actually succeeded.
            const applyUnitEntry = () => {
                setPackagingByAsin(prev => ({ ...prev, [asin]: unitEntry }));
                setPackagingPopoverAsin(null);
                setPackagingPopoverQty(null);
                // Same un-confirm hooks as the carton path (task 34 doctrine).
                setPoConfirmConfirmed(prev => {
                    if (!(asin in prev)) return prev;
                    const next = { ...prev };
                    delete next[asin];
                    return next;
                });
                // 2026-08-23 live fix: this used to DELETE the confirmation (task-34 un-confirm
                // hook), but ASIN mode has no re-confirm action -- the carton gate's "Review &
                // confirm" opened this modal, the operator applied, and the flag stayed false
                // forever. Applying from this modal IS the operator standing behind the numbers
                // (task 105's own definition); the bulk Amazon-prefill path bypasses this function
                // and stays unconfirmed.
                setPackagingConfirmedAsin(prev => ({ ...prev, [asin]: true }));
                onSaved(asin, unitEntry);
            };

            if (!alsoMakeDefault) {
                applyUnitEntry();
                return;
            }

            if (requirePersist) {
                // Finding 1 fix: await the POST FIRST -- on rejection leave the popover open,
                // never fire onSaved, and surface the failure via saveError instead of a toast.
                setPackagingSaving(true);
                setSaveError(null);
                try {
                    await ApiClient.post('/api/shipment-planner/packaging', postBody);
                    applyUnitEntry();
                } catch (err) {
                    console.error('Failed to save unit-as-box packaging default:', err);
                    setSaveError('Failed to save the packaging default: '
                        + (err.response?.data?.error || err.message));
                }
                setPackagingSaving(false);
                return;
            }

            // requirePersist:false, alsoMakeDefault:true -- planner behaviour, bit-for-bit
            // unchanged: apply/close/onSaved fire first, the POST is best-effort afterwards.
            applyUnitEntry();
            setPackagingSaving(true);
            try {
                await ApiClient.post('/api/shipment-planner/packaging', postBody);
            } catch (err) {
                console.error('Failed to save unit-as-box packaging default:', err);
                setToast('Packaging applied to this plan, but saving as the default failed: '
                    + (err.response?.data?.error || err.message));
            }
            setPackagingSaving(false);
            return;
        }

        const { parsed, errors } = validatePackagingForm(packagingForm);
        if (Object.keys(errors).length > 0) {
            setPackagingAttemptedSubmit(true);
            setToast(errors.loaded
                ? `Computed box weight is over the parcel ceiling: ${errors.loaded}`
                : errors.mode
                    ? errors.mode
                    : 'Unit weight, units per box, and a box from the catalog are all required.');
            return;
        }

        const derived = deriveLegacyPackagingFields(parsed.unitsPerBox, parsed.unitWeightLbs, selectedBox);
        const cartonEntry = {
            ...derived,
            unitWeightLbs: parsed.unitWeightLbs,
            boxCatalogId: selectedBox.id,
            boxName: selectedBox.name,
            tareWeightLbs: selectedBox.tareWeightLbs,
            computed: true,
            style: 'CARTON',
        };
        const postBody = {
            asin,
            packagingStyle: 'CARTON', // Task 67: explicit style (null would also mean carton)
            unitsPerBox: parsed.unitsPerBox,
            unitWeightLbs: parsed.unitWeightLbs,
            boxCatalogId: selectedBox.id,
            mode: shipmentMode, // Task 61: LTL saves skip the parcel ceiling server-side
        };
        // See applyUnitEntry above -- the same apply/close/un-confirm/onSaved bundle, for the
        // CARTON style. requirePersist:false runs it unconditionally before the best-effort POST;
        // requirePersist:true runs it only once the POST -- the entire operation on that host --
        // has actually succeeded.
        const applyCartonEntry = () => {
            setPackagingByAsin(prev => ({
                ...prev,
                [asin]: cartonEntry,
            }));
            setPackagingPopoverAsin(null);
            setPackagingPopoverQty(null);

            // Task 34: this popover is reused as the PO-confirm step's "Edit"/"Enter values"
            // action -- any value change un-confirms the row (both the in-modal per-row toggle
            // and, if the ASIN is already a plan row, its green-tick chip) until it's explicitly
            // re-confirmed.
            setPoConfirmConfirmed(prev => {
                if (!(asin in prev)) return prev;
                const next = { ...prev };
                delete next[asin];
                return next;
            });
            // 2026-08-23 live fix: see the UNIT branch above -- applying from the modal confirms.
            setPackagingConfirmedAsin(prev => ({ ...prev, [asin]: true }));
            onSaved(asin, cartonEntry);
        };

        if (!alsoMakeDefault) {
            applyCartonEntry();
            return;
        }

        if (requirePersist) {
            // Finding 1 fix: await the POST FIRST. On success, apply/close/onSaved as normal; on
            // rejection leave the popover open (packagingByAsin untouched, onSaved never fired)
            // and surface the failure via saveError instead of a toast the host would otherwise
            // have to pattern-match.
            setPackagingSaving(true);
            setSaveError(null);
            try {
                await ApiClient.post('/api/shipment-planner/packaging', postBody);
                applyCartonEntry();
            } catch (err) {
                console.error('Failed to save packaging default:', err);
                setSaveError('Failed to save the packaging default: '
                    + (err.response?.data?.error || err.message));
            }
            setPackagingSaving(false);
            return;
        }

        // requirePersist:false, alsoMakeDefault:true -- planner behaviour, bit-for-bit unchanged:
        // apply/close/onSaved fire first, the POST is best-effort afterwards.
        applyCartonEntry();
        setPackagingSaving(true);
        try {
            await ApiClient.post('/api/shipment-planner/packaging', postBody);
        } catch (err) {
            console.error('Failed to save packaging default:', err);
            setToast('Packaging applied to this plan, but saving as the default failed: '
                + (err.response?.data?.error || err.message));
        }
        setPackagingSaving(false);
    };

    // Task 41: modal context -- the open ASIN's current working value (for the "legacy" note when
    // it predates the two-tier model) and the live shipment-total math for the calling row's qty.
    const packagingModalActivePkg = packagingPopoverAsin ? packagingByAsin[packagingPopoverAsin] : null;
    const packagingModalLegacyOnly = isPackagingComplete(packagingModalActivePkg)
        && !packagingModalActivePkg.computed;
    const packagingModalTotals = packagingPopoverQty
        ? computeShipmentTotals(packagingPopoverQty, parseInt(packagingForm.unitsPerBox, 10),
            parseFloat(packagingForm.unitWeightLbs), selectedBox?.tareWeightLbs)
        : null;

    // Task 41: one shared-catalog carton card -- "Large · 18×18×32.5 · 4 lb tare", radio behavior,
    // hover X to delete (server refuses while any ASIN's saved default references it).
    const renderCatalogBoxCard = (box) => {
        const selected = selectedBoxId === box.id;
        return (
            <div
                key={box.id}
                onClick={() => { setSelectedBoxId(box.id); setNewBoxOpen(false); setEditingBoxId(null); }}
                className={`group relative flex items-start gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${
                    selected ? 'border-indigo-500 bg-indigo-50/60 ring-1 ring-indigo-500'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
            >
                <span className={`mt-0.5 flex-shrink-0 h-4 w-4 rounded-full border-2 flex items-center justify-center ${selected ? 'border-indigo-600' : 'border-gray-300'}`}>
                    {selected && <span className="h-2 w-2 rounded-full bg-indigo-600" />}
                </span>
                <div className="flex-1 min-w-0 pr-5">
                    <span className="font-semibold text-sm truncate text-gray-900">{box.name}</span>
                    <div className="text-xs text-gray-500 mt-0.5">
                        {box.boxLengthIn}×{box.boxWidthIn}×{box.boxHeightIn} in · {fmtLbsShort(box.tareWeightLbs)} lb tare
                    </div>
                </div>
                {/* 2026-09-01: hover pencil = edit this template (prefills the form, name
                    locked); hover X = delete. Both stopPropagation so neither selects the card. */}
                <button
                    onClick={(e) => { e.stopPropagation(); startEditCatalogBox(box); }}
                    title={`Edit box "${box.name}" — dims/tare apply everywhere it's used`}
                    className="absolute top-2 right-8 opacity-0 group-hover:opacity-100 focus:opacity-100 p-1 text-gray-400 hover:text-indigo-600 transition-opacity"
                >
                    <PencilIcon className="h-4 w-4" />
                </button>
                <button
                    onClick={(e) => { e.stopPropagation(); deleteCatalogBox(box); }}
                    disabled={boxDeletingId === box.id}
                    title={`Delete box "${box.name}" from the shared catalog`}
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 focus:opacity-100 p-1 text-gray-400 hover:text-red-600 disabled:opacity-50 transition-opacity"
                >
                    <XMarkIcon className="h-4 w-4" />
                </button>
            </div>
        );
    };

    return {
        // The 36-prop BoxConfigModal surface, minus packagingSummaryLine and fmtLbsShort (Task 1's
        // pure helpers, which the host still passes directly to avoid the pages/hooks cycle).
        packagingPopoverAsin,
        packagingPopoverTitle,
        packagingPopoverQty,
        packagingForm,
        packagingFieldErrors,
        packagingFormValid,
        packagingAttemptedSubmit,
        packagingStyleChoice,
        packagingSaving,
        packagingLoadedLbs,
        // Finding 1 fix: requirePersist:true's failure surface -- null except right after a
        // rejected persist-first POST (cleared on the next attempt and on open/close). Always
        // null under requirePersist:false.
        saveError,
        packagingModalActivePkg,
        packagingModalDerived,
        packagingModalLegacyOnly,
        packagingModalTotals,
        boxCatalog,
        choosePackagingStyle,
        closePackagingPopover,
        applyPackaging,
        updatePackagingField,
        newBoxOpen,
        newBoxForm,
        newBoxSaving,
        editingBoxId,
        boxError,
        boxNote,
        selectedBox,
        updateNewBoxField,
        saveNewBoxToCatalog,
        renderCatalogBoxCard,
        setEditingBoxId,
        setNewBoxForm,
        setBoxError,
        setNewBoxOpen,
        setSelectedBoxId,
        // Not modal props (BoxConfigModal never sees these) but needed by the host to drive the
        // modal from elsewhere on the page: opening it from a row/PO-confirm action, and
        // refreshing the shared catalog before the PO-confirm step's own summary render.
        openPackagingPopover,
        loadBoxCatalog,
    };
}
